import {useState, useCallback, useEffect} from 'react';

import {DebugPage} from '../debug-page';

import styles from './email-debug-page.module.css';

type FromAddressOption = {
    email: string;
    name: string;
    signature: string;
};

const FROM_ADDRESSES: FromAddressOption[] = [
    {
        email: 'sarah.chen@acmecorp.com',
        name: 'Sarah Chen',
        signature: `Best regards,\nSarah Chen\nSenior Account Executive\nAcme Corp\n(555) 123-4567`,
    },
    {
        email: 'mike.johnson@globex.io',
        name: 'Mike Johnson',
        signature: `Thanks,\nMike Johnson\nSales Manager\nGlobex Industries\nmike.johnson@globex.io`,
    },
    {
        email: 'jennifer.wu@initech.com',
        name: 'Jennifer Wu',
        signature: `Cheers,\nJennifer Wu\nBusiness Development\nInitech Solutions\nwww.initech.com`,
    },
    {
        email: 'david.miller@wayneent.com',
        name: 'David Miller',
        signature: `Regards,\nDavid Miller\nPartnership Lead\nWayne Enterprises\n(555) 987-6543`,
    },
    {
        email: 'support@randomvendor.net',
        name: 'Random Vendor Support',
        signature: `Thank you for your inquiry.\n\nRandom Vendor Support Team\nsupport@randomvendor.net\n1-800-VENDOR-1`,
    },
];

const SAMPLE_SUBJECTS = [
    'Quick question about your product',
    'Following up on our conversation',
    'Partnership opportunity',
    'Request for pricing information',
    'Demo request for your team',
    'Interested in learning more',
];

const SAMPLE_BODIES = [
    `Hi there,

I came across your company and wanted to reach out. We're currently evaluating solutions in your space and I'd love to learn more about what you offer.

Could we schedule a quick call this week to discuss?`,
    `Hello,

Following up on my previous email. I wanted to check if you had a chance to review the information I sent over.

Let me know if you have any questions or if there's a better time to connect.`,
    `Hi,

I'm reaching out because I think there could be a great opportunity for our companies to work together.

We've been helping businesses like yours streamline their operations and I'd love to share some ideas.

Would you be open to a brief conversation?`,
];

function getRandomItem<T>(arr: T[]): T | undefined {
    return arr[Math.floor(Math.random() * arr.length)];
}

type SendResult = {
    success: boolean;
    type?: string;
    messageId?: number;
    emailId?: number;
    error?: string;
    mailpitSent?: boolean;
};

async function sendTestEmail(params: {
    fromEmail: string;
    fromName: string;
    to: string;
    subject: string;
    body: string;
    sendViaMailpit: boolean;
}): Promise<SendResult> {
    const response = await fetch('/internal-debug/send-test-email', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
    }

    return response.json();
}

async function fetchSharedInboxAddress(): Promise<string | null> {
    try {
        const response = await fetch('/internal-debug/send-test-email', {
            headers: {'X-Requested-With': 'XMLHttpRequest'},
            credentials: 'same-origin',
        });

        if (!response.ok) return null;
        const data = await response.json();

        return data.sharedInboxAddress ?? null;
    } catch {
        return null;
    }
}

export function EmailDebugPage(): React.ReactElement {
    const [fromAddress, setFromAddress] = useState<FromAddressOption>(FROM_ADDRESSES[0]!);
    const [toAddress, setToAddress] = useState('');
    const [subject, setSubject] = useState(SAMPLE_SUBJECTS[0]);
    const [body, setBody] = useState(SAMPLE_BODIES[0]);
    const [signature, setSignature] = useState(FROM_ADDRESSES[0]!.signature);
    const [isSending, setIsSending] = useState(false);
    const [lastSent, setLastSent] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [sendViaMailpit, setSendViaMailpit] = useState(true);

    useEffect(() => {
        fetchSharedInboxAddress().then((address) => {
            if (address) {
                setToAddress(address);
            }
        });
    }, []);

    const handleFromChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = FROM_ADDRESSES.find((addr) => addr.email === e.target.value);
        if (selected) {
            setFromAddress(selected);
            setSignature(selected.signature);
        }
    }, []);

    const handleRandomize = useCallback(() => {
        const randomFrom = getRandomItem(FROM_ADDRESSES);
        setFromAddress(randomFrom!);
        setSignature(randomFrom!.signature);
        setSubject(getRandomItem(SAMPLE_SUBJECTS));
        setBody(getRandomItem(SAMPLE_BODIES));
    }, []);

    const handleSend = useCallback(async () => {
        setIsSending(true);
        setError(null);
        setSuccessMessage(null);

        const fullBody = signature ? `${body}\n\n--\n${signature}` : body ?? '';

        try {
            const result = await sendTestEmail({
                fromEmail: fromAddress.email,
                fromName: fromAddress.name,
                to: toAddress,
                subject: subject ?? '',
                body: fullBody,
                sendViaMailpit,
            });

            if (result.success) {
                setLastSent(new Date().toLocaleTimeString());

                const pipelineDetail =
                    result.type === 'shared_inbox'
                        ? `Shared inbox message created (ID: ${result.messageId})`
                        : `Email saved (ID: ${result.emailId})`;
                const mailpitDetail = result.mailpitSent ? ' + sent to Mailpit' : '';
                setSuccessMessage(pipelineDetail + mailpitDetail);
            } else {
                setError(result.error ?? 'Failed to send email');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Network error');
        } finally {
            setIsSending(false);
        }
    }, [fromAddress, toAddress, subject, body, signature, sendViaMailpit]);

    const toolbar = (
        <>
            <span className={styles['user-info']}>
                {NutClientConfig.loggedInUserName} ({NutClientConfig.loggedInUserEmail})
            </span>
            {lastSent && <span className={styles['last-sent']}>Last sent: {lastSent}</span>}
        </>
    );

    return (
        <DebugPage
            title='Email Debug'
            subtitle='Send test emails to the shared email inbox for debugging purposes.'
            toolbar={toolbar}
        >
            <div className={styles['email-form']}>
                <div className={styles['form-row']}>
                    <label className={styles.label}>From:</label>
                    <select
                        className={styles.select}
                        value={fromAddress.email}
                        onChange={handleFromChange}
                    >
                        {FROM_ADDRESSES.map((addr) => (
                            <option key={addr.email} value={addr.email}>
                                {addr.name} &lt;{addr.email}&gt;
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles['form-row']}>
                    <label className={styles.label}>To:</label>
                    <input
                        type='email'
                        className={styles.input}
                        value={toAddress}
                        onChange={(e) => setToAddress(e.target.value)}
                        placeholder='ibx_i{instanceId}_{hash}@nutshellemail.com'
                    />
                </div>

                <div className={styles['form-row']}>
                    <label className={styles.label}>Subject:</label>
                    <input
                        type='text'
                        className={styles.input}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder='Enter subject line'
                    />
                </div>

                <div className={styles['form-row']}>
                    <label className={styles.label}>Body:</label>
                    <textarea
                        className={styles.textarea}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={8}
                        placeholder='Write your email here...'
                    />
                </div>

                <div className={styles['form-row']}>
                    <label className={styles.label}>Signature:</label>
                    <textarea
                        className={styles['textarea-signature']}
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        rows={5}
                        placeholder='Email signature'
                    />
                </div>

                <div className={styles['form-actions']}>
                    <label className={styles['checkbox-label']}>
                        <input
                            type='checkbox'
                            checked={sendViaMailpit}
                            onChange={(e) => setSendViaMailpit(e.target.checked)}
                        />
                        Also send to Mailpit
                    </label>
                    <button
                        type='button'
                        className={styles['randomize-button']}
                        onClick={handleRandomize}
                    >
                        Randomize
                    </button>
                    <button
                        type='button'
                        className={styles['send-button']}
                        onClick={handleSend}
                        disabled={isSending || !toAddress.trim()}
                    >
                        {isSending ? 'Sending...' : 'Send Test Email'}
                    </button>
                </div>

                {error && <div className={styles['status-error']}>{error}</div>}
                {successMessage && <div className={styles['status-success']}>{successMessage}</div>}

                <div className={styles.preview}>
                    <div className={styles['preview-header']}>Preview</div>
                    <div className={styles['preview-content']}>
                        <div className={styles['preview-meta']}>
                            <strong>From:</strong> {fromAddress.name} &lt;{fromAddress.email}&gt;
                        </div>
                        <div className={styles['preview-meta']}>
                            <strong>To:</strong> {toAddress}
                        </div>
                        <div className={styles['preview-meta']}>
                            <strong>Subject:</strong> {subject}
                        </div>
                        <hr className={styles['preview-divider']} />
                        <div className={styles['preview-body']}>
                            {body}
                            {signature && (
                                <>
                                    <br />
                                    <br />
                                    --
                                    <br />
                                    {signature}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DebugPage>
    );
}
